import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fontStyles } from "components/Common/commonStyles";

// ASTT-7319: shared by Training Notes and Hand-off Report's own Preview
// buttons (and View DTRs' per-row Preview button), per direct
// stakeholder-demo feedback -- fetchPreview is the one thing that
// differs between callers (previewDtrEmail vs previewHandOffEmail),
// injected by whichever caller opens this, so the dialog itself has no
// idea which kind of entry it's actually previewing.
//
// The email body renders inside an iframe (srcDoc), not the
// dangerouslySetInnerHTML-in-a-span pattern RichTextEditorField.js's own
// RichPreview uses -- deliberately different, since that pattern is for
// a plain HTML fragment rendered inline within the app's own styling.
// This is a full, already-rendered EMAIL document (BuildDtrEmailContent/
// EmbedInlineImagesAsync's output), which can carry its own complete
// inline-styled table layout (standard for email-client compatibility)
// that would either break or bleed into the app's own styles if injected
// directly into this page's DOM instead of isolated in its own document.
export default function EmailPreviewDialog({ open, onClose, fetchPreview }) {
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetchPreview?.();
        if (cancelled) return;
        if (response?.statusCode === 200 && response?.data) {
          setSubject(response.data.emailSubject ?? response.data.EmailSubject ?? "");
          setMessageBody(response.data.messageBody ?? response.data.MessageBody ?? "");
        } else {
          setError("Unable to build a preview for this entry.");
        }
      } catch (err) {
        if (!cancelled) {
          setError("Unable to build a preview for this entry.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, fetchPreview]);

  const handleClose = () => {
    // Reset on close so a stale previous preview never flashes before
    // the next fetch resolves, if this same dialog instance is reused
    // for a different entry right after.
    setSubject("");
    setMessageBody("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ ...fontStyles, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {subject ? `Preview — ${subject}` : "Email Preview"}
        <IconButton aria-label="Close" size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ ...fontStyles, textAlign: "center", py: 6, color: "#9e9e9e" }}>
            {error}
          </Box>
        ) : (
          <iframe
            title="Email preview"
            srcDoc={messageBody}
            // ASTT-7319 follow-up: defense-in-depth, found during a
            // pre-commit review sweep -- this is a pure read-only
            // preview, so no sandbox permission (scripts, forms, popups,
            // same-origin, etc.) is actually needed here. The email body
            // is already sanitized upstream via sanitizeEditorHtml before
            // it's ever saved, but an empty sandbox costs nothing and
            // means this preview specifically can never execute script
            // even if something unexpected slipped through further up
            // the chain.
            sandbox=""
            style={{
              width: "100%",
              height: "70vh",
              border: "none",
              display: "block",
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

EmailPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fetchPreview: PropTypes.func,
};
